import AsyncStorage from "@react-native-async-storage/async-storage";
import { Fragment, useEffect, useState } from "react";
import {
    ActivityIndicator,
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
} from "react-native";
import { useTranslation } from "react-i18next";
import { AppBar } from "../components";
import i18n from "../translation/index";
import { CheckCircleIcon } from "../assets/icons";
import { useNavigation } from "@react-navigation/native";
import { getDetailSurvey, getOptions, getQuizIndicatorStatistics } from "../services/survey";
import { buildStudentStatistic } from "../Utils/characterStatistic";

const FILTER_ALL = "Semua";
const FILTER_FILLED = "Sudah Terisi";
const FILTER_EMPTY = "Belum Terisi";
const FILTERS = [FILTER_ALL, FILTER_FILLED, FILTER_EMPTY];

const SurveyKarakterList = ({ route }) => {
    const { params } = route;
    const { t } = useTranslation();
    const navigation = useNavigation();
    const [loading, setLoading] = useState(true);
    const [studentData, setStudentData] = useState([]);
    const [loadError, setLoadError] = useState(false);
    const [activeFilter, setActiveFilter] = useState(FILTER_ALL);

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        const loadStudentOptions = async () => {
            setLoading(true);
            setLoadError(false);
            setStudentData([]);
            try {
                if (!params?.additionalContentId) throw new Error("Missing additionalContentId");
                const [detailResponse, statisticResponse] = await Promise.all([
                    getDetailSurvey(params?.quizId),
                    getQuizIndicatorStatistics(params.additionalContentId, controller.signal),
                ]);
                console.log("[SurveyKarakterList] quizId:", params?.quizId);
                console.log(
                    "[SurveyKarakterList] detail response:",
                    detailResponse?.data,
                );
                const dataSources = (detailResponse?.data || [])
                    .filter((item) => /murid|siswa|anak/i.test(String(item.title || "")))
                    .flatMap((item) => {
                        try {
                            const options = JSON.parse(item.detail || "{}")?.options;
                            return Array.isArray(options) ? options : [options];
                        } catch (error) {
                            return [];
                        }
                    })
                    .filter(
                        (option) =>
                            typeof option === "string" && option.endsWith(".json"),
                    )
                    .map((option) => option.replace(/\.json$/, ""));
                const uniqueDataSources = [...new Set(dataSources)];
                console.log(
                    "[SurveyKarakterList] data sources from detail.options:",
                    uniqueDataSources,
                );
                const optionResponses = await Promise.all(
                    uniqueDataSources.map((source) => getOptions(source)),
                );
                console.log(
                    "[SurveyKarakterList] data-source responses:",
                    optionResponses.map((response) => response?.data),
                );
                const names = [
                    ...new Set(
                        optionResponses.flatMap((response) =>
                            Array.isArray(response?.data?.options)
                                ? response.data.options
                                : [],
                        ),
                    ),
                ];
                console.log("[SurveyKarakterList] options used as names:", names);
                if (isMounted) {
                    setStudentData(
                        names.map((name, index) => ({
                            id: `${name}-${index}`,
                            ...buildStudentStatistic(statisticResponse.data, name, params.additionalContentId),
                        })),
                    );
                }
            } catch (error) {
                console.error("[SurveyKarakterList] failed to load options:", error);
                if (isMounted) {
                    setLoadError(true);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        loadStudentOptions();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [params?.quizId, params?.additionalContentId]);

    useEffect(() => {
        async function getLang() {
            return await AsyncStorage.getItem("lang");
        }

        getLang().then((res) => {
            if (!res) {
                AsyncStorage.setItem("lang", "id");
                i18n.changeLanguage("id");
                return;
            }
            AsyncStorage.setItem("lang", res);
            i18n.changeLanguage(res);
        });
    }, []);

    const filteredData = studentData.filter((item) => {
        if (activeFilter === FILTER_FILLED) return item.isAnswered;
        if (activeFilter === FILTER_EMPTY) return !item.isAnswered;
        return true;
    });

    return (
        <Fragment>
            <AppBar title={"Capaian Karakter"} isBack={true} />
            {loading ? (
                <View style={styles.loading}>
                    <ActivityIndicator color="#6724E2" />
                </View>
            ) : (
                <View style={{ flex: 1, backgroundColor: "#FFF" }}>
                    <View style={styles.filterRow}>
                        {FILTERS.map((filter) => (
                            <TouchableOpacity
                                key={filter}
                                style={[
                                    styles.filterBtn,
                                    activeFilter === filter && styles.filterBtnActive,
                                ]}
                                onPress={() => setActiveFilter(filter)}
                            >
                                <Text
                                    style={[
                                        styles.filterTx,
                                        activeFilter === filter && styles.filterTxActive,
                                    ]}
                                >
                                    {filter}
                                </Text>
                            </TouchableOpacity>
                        ))}
                    </View>

                    <ScrollView contentContainerStyle={styles.listContainer}>
                        {loadError && <Text style={styles.emptyTx}>Data capaian siswa gagal dimuat. Silakan coba kembali.</Text>}
                        {!loadError && filteredData.length === 0 && <Text style={styles.emptyTx}>Belum ada data siswa untuk filter ini.</Text>}
                        {filteredData.map((item) => (
                            <TouchableOpacity
                                key={item.id}
                                style={styles.card}
                                activeOpacity={0.7}
                                onPress={() => {
                                    navigation.navigate("SurveyKarakterDetail", {
                                        ...params,
                                        studentId: item.id,
                                        studentName: item.name,
                                        studentStatistic: item,
                                    });
                                }}
                            >
                                <View style={styles.cardRow}>
                                    <CheckCircleIcon
                                        fill={item.isAnswered ? "#4EB826" : "#C2C2C2"}
                                        width={32}
                                        height={32}
                                    />

                                    <View style={styles.cardContent}>
                                        <Text style={[
                                            styles.studentName,
                                            item.isAnswered && styles.studentNameActive,
                                        ]}>
                                            {item.name}
                                        </Text>
                                        {item.isAnswered ? (
                                            <View style={styles.statsRow}>
                                                <Text style={styles.statsTx}>
                                                    {item.karakterTerisi} dari {item.totalKarakter} Karakter Terisi
                                                </Text>
                                                <View style={styles.dot} />
                                                <Text style={styles.statsTx}>
                                                    {item.indikatorTerisi} dari {item.totalIndikator} Indikator Terisi
                                                </Text>
                                            </View>
                                        ) : (
                                            <Text style={styles.emptyTx}>Indikator belum diisi</Text>
                                        )}
                                    </View>
                                </View>
                            </TouchableOpacity>
                        ))}
                    </ScrollView>
                </View>
            )}
        </Fragment>
    );
};

const styles = StyleSheet.create({
    loading: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#FFF",
    },
    filterRow: {
        flexDirection: "row",
        gap: 8,
        paddingHorizontal: 16,
        paddingVertical: 14,
        backgroundColor: "#FFF",
    },
    filterBtn: {
        paddingHorizontal: 16,
        paddingVertical: 8,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: "#EAEAEA",
        backgroundColor: "#FFF",
    },
    filterBtnActive: {
        backgroundColor: "#6724E2",
        borderColor: "#6724E2",
    },
    filterTx: {
        fontFamily: "Archivo-Medium",
        fontSize: 13,
        color: "#333",
    },
    filterTxActive: {
        color: "#FFF",
        fontFamily: "Archivo-SemiBold",
    },
    listContainer: {
        paddingHorizontal: 16,
        paddingBottom: 24,
        gap: 10,
    },
    card: {
        borderWidth: 1,
        borderColor: "#EAEAEA",
        borderRadius: 10,
        paddingHorizontal: 14,
        paddingVertical: 14,
        backgroundColor: "#FFF",
    },
    cardRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
    },
    cardContent: {
        flex: 1,
        gap: 4,
    },
    studentName: {
        fontFamily: "Archivo-SemiBold",
        fontSize: 13,
        color: "#323232",
    },
    studentNameActive: {
        color: "#4EB826",
    },
    statsRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        flexWrap: "wrap",
    },
    statsTx: {
        fontFamily: "Archivo-Regular",
        fontSize: 11,
        color: "#555",
    },
    dot: {
        width: 4,
        height: 4,
        borderRadius: 2,
        backgroundColor: "#555",
    },
    emptyTx: {
        fontFamily: "Archivo-Regular",
        fontSize: 12,
        color: "#999",
    },
});

export default SurveyKarakterList;